import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ReactTags, Tag } from 'react-tag-autocomplete'
import { useEditNode } from '../utils/query';
import { ProfileData } from './NodeProfileCard';
import { useQueryClient } from '@tanstack/react-query';

interface ProfileFormData {
    id: string;
    name: string;
    subtitle?: string;
    description?: string;
    tags: string[];
    tagsV2: Tag[];
}

interface NodeProfileEditFormProps {
    initialData: ProfileFormData;
    rawData: ProfileData;
    onSubmit: (data: ProfileFormData) => void;
}

const NodeProfileEditForm: React.FC<NodeProfileEditFormProps> = ({
    initialData,
    rawData,
    onSubmit,
}) => {
    const { register, handleSubmit, setValue } = useForm<ProfileFormData>({
        defaultValues: initialData,
    });
    const [tagsV2, setTagsV2] = useState<Tag[]>(
        initialData.tagsV2 || []
    );
    const [loading, setLoading] = useState(false);

    const queryClient = useQueryClient()

    useEffect(() => {
        setValue('tagsV2', tagsV2);
    }, [tagsV2, setValue]);

    const editMutation = useEditNode();
    const submitHandler = async (data: ProfileFormData) => {
        console.log('Submitting:', data);
        data.tags = data.tagsV2.map((tag) => tag.label.trim());
        // @ts-ignore
        delete data.tagsV2;


        //TODO: Review
        setLoading(true);
        // @ts-ignore
        await editMutation.mutate({ nodeId: data.id, newNode: { ...rawData, ...data } });
        await queryClient.invalidateQueries({ queryKey: ['nodes'] });
        await queryClient.invalidateQueries({ queryKey: ['node'] });
        setLoading(false);

        onSubmit(data);
    };

    return (
        <form onSubmit={handleSubmit(submitHandler)} className="flex flex-col gap-4">
            <div>
                <label className="block font-semibold">Name</label>
                <input
                    {...register('name', { required: true })}
                    className="input input-bordered w-full"
                />
            </div>
            <div>
                <label className="block font-semibold">Subtitle</label>
                <input
                    {...register('subtitle')}
                    className="input input-bordered w-full"
                />
            </div>
            <div>
                <label className="block font-semibold">Description</label>
                <textarea
                    {...register('description')}
                    className="textarea textarea-bordered w-full"
                />
            </div>
            <div>
                <label className="block font-semibold">Tags v2(comma-separated)</label>
                <ReactTags
                    allowNew
                    allowResize={false}
                    classNames={{
                        root: 'react-tags',
                        rootIsActive: 'is-active',
                        rootIsDisabled: 'is-disabled',
                        rootIsInvalid: 'is-invalid',
                        label: 'react-tags__label',
                        tagList: 'flex flex-row react-tags__list',
                        tagListItem: 'react-tags__list-item',
                        tag: 'badge react-tags__tag',
                        tagName: 'react-tags__tag-name',
                        comboBox: 'react-tags__combobox',
                        input: 'react-tags__combobox-input',
                        listBox: 'react-tags__listbox',
                        option: 'react-tags__listbox-option',
                        optionIsActive: 'is-active',
                        highlight: 'react-tags__listbox-option-highlight',
                    }}
                    labelText="Select countries"
                    selected={tagsV2}
                    suggestions={[]}
                    onAdd={(tag: Tag) => {
                        setTagsV2([...tagsV2, tag]);
                    }}
                    onDelete={(i) => {
                        setTagsV2(tagsV2.filter((_, index) => index !== i));
                    }}
                    noOptionsText="No matching countries"
                />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading && <span className="loading loading-spinner"></span>}
                Save
            </button>
        </form>
    );
};

export default NodeProfileEditForm;